import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateEventInput, EventInstance, StoredEvent, UpdateEventInput } from '@td/shared';
import { get, post, patch, del } from '../lib/api';

function useInvalidate() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['events'] });
    void qc.invalidateQueries({ queryKey: ['dashboard'] });
  };
}

export function useEvents(from: string, to: string) {
  return useQuery({
    queryKey: ['events', from, to],
    queryFn: () => get<EventInstance[]>(`/events?from=${from}&to=${to}`),
    staleTime: 30_000,
  });
}

export function useEvent(id: string | null) {
  return useQuery({
    queryKey: ['event', id],
    queryFn: () => get<StoredEvent>(`/events/${id}`),
    enabled: !!id,
  });
}

export function useCreateEvent() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: CreateEventInput) => post<StoredEvent>('/events', input),
    onSuccess: invalidate,
  });
}

export function useUpdateEvent() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, ...input }: UpdateEventInput & { id: string }) =>
      patch<StoredEvent>(`/events/${id}`, input),
    onSuccess: invalidate,
  });
}

export function useDeleteEvent() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({
      id,
      scope,
      date,
    }: {
      id: string;
      scope: 'occurrence' | 'series';
      date?: string;
    }) => del<void>(`/events/${id}?scope=${scope}${date ? `&date=${date}` : ''}`),
    onSuccess: invalidate,
  });
}
